import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import type { LocationQuery, LocationQueryValue } from 'vue-router'
import { Address } from './global'

export const SupportedLocales = ['en', 'nl', 'fr'] as const

export enum PartnerType {
    Kbc = 'kbc',
    EnergyAtHome = 'energyAtHome',
    Generic = 'generic',
}

export enum Platform {
    Kbc = 'kbc',
    Web = 'web',
}

export enum Audience {
    Homeowners = 'homeowners',
    BusinessOwners = 'business_owners',
}

/**
 * @returns {string} - The timezone offset in the format (+|-)HH:mm
 * @example +02:00
 */
export const getTimezoneOffset = (): string => {
    const offset: number = new Date().getTimezoneOffset()
    const absOffset: number = Math.abs(offset)
    return (
        (offset > 0 ? '-' : '+') +
        ('00' + Math.floor(absOffset / 60)).slice(-2) +
        ':' +
        ('00' + (absOffset % 60)).slice(-2)
    )
}

export const toAtomString = (date: Date): string =>
    date.toISOString().slice(0, 19) + getTimezoneOffset()

export const formatAddress = (address: Address): string => {
    const { street, number, extension, postalCode, city } = address
    let formattedAddress = `${street} ${number}`

    if (extension) {
        formattedAddress += ` ${extension}`
    }

    formattedAddress += `, ${postalCode} ${city}`

    return formattedAddress
}

export const firstQueryValue = (
    value: LocationQueryValue | LocationQueryValue[] | undefined
): string | undefined => {
    const single = Array.isArray(value) ? value[0] : value

    return single ? single.toString() : undefined
}

export type MarketingSourceFromQuery = {
    utmSource?: string
    utmMedium?: string
    utmCampaign?: string
    utmContent?: string
    utmTerm?: string
    utmId?: string
    utmSourcePlatform?: string
    gclid?: string
    fbclid?: string
    msclkid?: string
    clientId?: string
    sessionId?: string
    referer?: string
}

export const marketingSourceFromQuery = (query: LocationQuery): MarketingSourceFromQuery => ({
    utmSource: firstQueryValue(query.utm_source),
    utmMedium: firstQueryValue(query.utm_medium),
    utmCampaign: firstQueryValue(query.utm_campaign),
    utmContent: firstQueryValue(query.utm_content),
    utmTerm: firstQueryValue(query.utm_term),
    utmId: firstQueryValue(query.utm_id),
    utmSourcePlatform: firstQueryValue(query.utm_source_platform),
    gclid: firstQueryValue(query.gclid),
    fbclid: firstQueryValue(query.fbclid),
    msclkid: firstQueryValue(query.msclkid),
    clientId: firstQueryValue(query.client_id),
    sessionId: firstQueryValue(query.session_id),
    referer: firstQueryValue(query.referer) || document.referrer || undefined,
})

export function handleError(error: any, defaultMessage?: string): string | undefined {
    return error?.response?.status === 422
        ? error?.response?.data?.translated_message ||
              error?.response?.data?.message ||
              defaultMessage
        : error?.message || defaultMessage
}

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs))
}
